<script lang="ts">
  import { onMount, tick } from 'svelte'
  import { IT_CPV_CODES } from '../data/cpv'
  import { loadCpvCodes } from '../stores/cpvCodes'
  import { cpvLabel } from '../composables/cpvLabel'
  import { locale, type Locale } from '../i18n'
  import type { CpvCode } from '../api/cpvCodes'

  interface Props {
    value: string | undefined
    placeholder?: string
    id?: string
  }

  let { value = $bindable(), placeholder, id }: Props = $props()

  let open = $state(false)
  let activeIndex = $state(-1)
  let wrapperEl = $state<HTMLDivElement | undefined>(undefined)
  let menuEl = $state<HTMLUListElement | undefined>(undefined)
  const listboxId = `cpv-combo-${Math.random().toString(36).slice(2, 10)}`

  // The catalogue is only ever used to enrich suggestions with the official
  // description — the field stays a usable free-text input whether this is
  // still loading, empty (fetch failed), or populated. No error is surfaced
  // to the user for a suggestions-only dependency.
  let catalog = $state<CpvCode[]>([])
  const itScope = new Set(IT_CPV_CODES)

  onMount(() => {
    void loadCpvCodes()
      .then((c) => {
        catalog = c
      })
      .catch(() => {
        // Suggestions simply don't appear; the input remains a plain text field.
      })
  })

  // Keeps the empty-input default list small and IT-focused instead of
  // dumping the full ~9,450-code catalogue into the dropdown.
  const itCodes = $derived(catalog.filter((c) => itScope.has(c.code)))

  const query = $derived((value ?? '').trim().toLowerCase())

  // Caps how many suggestions ever render — a broad query against the full
  // catalogue (e.g. a single digit) could otherwise match thousands of rows.
  const MAX_SUGGESTIONS = 50

  // Matches on the code or on the description in the active UI language.
  function search(list: CpvCode[], q: string, lang: Locale): CpvCode[] {
    return list
      .filter((e) => e.code.startsWith(q) || cpvLabel(e, lang).toLowerCase().includes(q))
      .slice(0, MAX_SUGGESTIONS)
  }

  const matches = $derived.by((): CpvCode[] => {
    if (!query) return itCodes
    const itMatches = search(itCodes, query, $locale)
    // A code/text with no hit inside the IT scope may still be a real,
    // valid CPV entry (e.g. a non-IT code typed on purpose) — fall back to
    // the full catalogue rather than leaving the user with nothing, without
    // ever showing the full catalogue unfiltered.
    return itMatches.length > 0 ? itMatches : search(catalog, query, $locale)
  })

  // Keep the active option in range whenever the match list changes size —
  // including when the catalogue arrives asynchronously after the first
  // render and `matches` grows from empty to populated.
  $effect(() => {
    if (activeIndex >= matches.length) {
      activeIndex = matches.length > 0 ? matches.length - 1 : -1
    }
  })

  function pick(entry: CpvCode) {
    value = entry.code
    open = false
    activeIndex = -1
  }

  function onInput(e: Event) {
    value = (e.currentTarget as HTMLInputElement).value
    open = true
    activeIndex = -1
  }

  function scrollActiveIntoView() {
    const item = menuEl?.children[activeIndex] as HTMLElement | undefined
    if (!menuEl || !item) return
    const menuRect = menuEl.getBoundingClientRect()
    const itemRect = item.getBoundingClientRect()
    if (itemRect.top < menuRect.top) {
      menuEl.scrollTop -= menuRect.top - itemRect.top
    } else if (itemRect.bottom > menuRect.bottom) {
      menuEl.scrollTop += itemRect.bottom - menuRect.bottom
    }
  }

  async function onKeydown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      open = true
      if (matches.length > 0) {
        activeIndex = Math.min(matches.length - 1, activeIndex + 1)
      }
      await tick()
      scrollActiveIntoView()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (matches.length > 0) {
        activeIndex = Math.max(0, activeIndex - 1)
      }
      await tick()
      scrollActiveIntoView()
    } else if (e.key === 'Enter') {
      const selected = matches[activeIndex]
      if (open && selected) {
        e.preventDefault()
        pick(selected)
      }
    } else if (e.key === 'Escape') {
      open = false
    }
  }

  function onFocusOut(e: FocusEvent) {
    const next = e.relatedTarget as Node | null
    if (next && wrapperEl?.contains(next)) return
    open = false
  }
</script>

<div class="combo" bind:this={wrapperEl} onfocusout={onFocusOut}>
  <input
    {id}
    class="input"
    type="text"
    role="combobox"
    aria-autocomplete="list"
    aria-expanded={open}
    aria-controls={listboxId}
    autocomplete="off"
    {placeholder}
    value={value ?? ''}
    oninput={onInput}
    onfocus={() => (open = true)}
    onkeydown={onKeydown}
  />
  {#if open && matches.length > 0}
    <ul class="combo__menu" role="listbox" id={listboxId} bind:this={menuEl}>
      {#each matches as entry, i (entry.code)}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <li
          class="combo__item"
          class:combo__item--active={i === activeIndex}
          role="option"
          aria-selected={i === activeIndex}
          onmousedown={(e) => e.preventDefault()}
          onclick={() => pick(entry)}
        >
          <span class="combo__code">{entry.code}</span>
          <span class="combo__desc">{cpvLabel(entry, $locale)}</span>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style lang="scss">
  @use '../assets/styles/variables' as v;

  .combo {
    position: relative;
  }

  .combo__menu {
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    right: 0;
    z-index: 20;
    list-style: none;
    padding: v.$space-xs 0;
    margin: 0;
    max-height: 16rem;
    overflow-y: auto;
    background: v.$color-surface-elevated;
    border: 1px solid v.$color-border-strong;
    border-radius: v.$radius-sm;
    box-shadow: v.$shadow-md;
  }

  .combo__item {
    display: flex;
    gap: v.$space-sm;
    align-items: baseline;
    padding: v.$space-xs v.$space-md;
    cursor: pointer;
    font-size: 0.8125rem;

    &:hover,
    &--active {
      @include v.interactive-hover;
    }
  }

  .combo__code {
    font-family: v.$font-mono;
    color: v.$color-text;
    min-width: 5.5rem;
    flex-shrink: 0;
  }

  .combo__desc {
    color: v.$color-text-muted;
  }
</style>
